/**
 * Phase 4.0 / 4.1.1 — KRO Phone System v2: WebRTC singleton client.
 *
 * ════════════════════════════════════════════════════════════════════
 *  ★ STABLE OUTBOUND BASELINE — iter_196 (2026-05-02) ★
 *
 *  Outbound WebRTC has been verified end-to-end live by the operator:
 *    - "Call from browser" works
 *    - Customer sees the company KRO number as caller-ID
 *    - Audio works both directions
 *    - ActiveCallBar stays visible for the entire call
 *    - Hang Up button is always accessible
 *    - call_ended_webrtc bubble appears on completion
 *
 *  ⚠️  DO NOT MODIFY `_handleNotification`, `_pendingDial`,
 *      `_answeredCallIds`, the auto-answer block, or the
 *      `hangup/destroy/purge` state-machine handler when working
 *      on inbound features.  Future inbound work MUST add new code
 *      paths (e.g. dedicated inbound-only methods, gated on a
 *      separate flag) without touching these critical sections.
 *
 *  The iter_193 fix (post-bridge `sdkDetached: true` + backend-
 *  authoritative `webrtc_active/{uid}` deletion as the sole UI
 *  teardown trigger) is what makes outbound stable. Adding
 *  conditional branches inside `_handleNotification` introduces
 *  timing-sensitive races (see iter_195 regression).
 * ════════════════════════════════════════════════════════════════════
 *
 * Backend-mediated outbound dial
 * ------------------------------
 * Instead of the browser originating a SIP INVITE with a user-controlled
 * `callerNumber` (spoofable via dev-tools), we now:
 *
 *   1. Browser POSTs `/api/webrtc/initiate { destination, conversationId }`.
 *   2. Backend resolves the authoritative caller-ID from the tenant's
 *      channel registry and rings the operator's own browser via SIP.
 *   3. Our SDK notification stream surfaces that incoming call; we
 *      auto-answer it immediately.
 *   4. Telnyx, on our leg-A answer, dials the customer (leg B) with the
 *      backend-set `from` and `link_to`, auto-bridging on pickup.
 *
 * The frontend NEVER passes `callerNumber` on newCall — tenant isolation
 * is enforced server-side.
 *
 * State shape (passed to subscribers)
 * -----------------------------------
 *   {
 *     status: 'idle' | 'connecting' | 'registered' | 'error',
 *     displayCallerId: string | null,
 *     activeCall: {
 *       callId, state, destination, conversationId, surface,
 *       startedAt, endedAt, durationSecs, remoteStream,
 *     } | null,
 *     error: string | null,
 *   }
 */
import { TelnyxRTC } from '@telnyx/webrtc';
import { getAuth } from 'firebase/auth';

const API = process.env.REACT_APP_BACKEND_URL || '';

let _instance = null;

class WebRTCManager {
  constructor() {
    this._client = null;
    this._activeCall = null;
    this._listeners = new Set();
    this._connectingPromise = null;

    this._state = {
      status: 'idle',
      displayCallerId: null,
      companyId: null,
      uid: null,
      activeCall: null,
      // Phase 4.2b — incoming customer call awaiting operator action.
      // Populated by `_handleInboundRing` (separate method, never
      // touches `_handleNotification` critical path). Cleared on
      // accept/reject/SDK destroy.
      incomingCall: null,
      error: null,
    };

    this._tickInterval = null;
    this._startedLogged = false;
    this._pendingDial = null;
    // Track which inbound call IDs we've already answered so the auto-
    // answer is idempotent across the multiple notifications the SDK
    // fires per call lifecycle (trying → requesting → ringing → active).
    this._answeredCallIds = new Set();
    // Phase 4.2b — call IDs the dispatcher has classified as
    // customer-inbound rings. Used so post-accept SDK notifications
    // bypass the inbound dispatcher and flow into the regular
    // `_handleNotification` state machine.
    this._inboundRingCallIds = new Set();
  }

  getState() { return this._state; }

  subscribe(listener) {
    this._listeners.add(listener);
    try { listener(this._state); } catch (_) { /* swallow */ }
    return () => { this._listeners.delete(listener); };
  }

  _emit() {
    for (const l of this._listeners) {
      try { l(this._state); } catch (_) { /* swallow */ }
    }
  }

  _patch(partial) {
    this._state = { ...this._state, ...partial };
    this._emit();
  }

  _patchCall(partial) {
    if (!this._state.activeCall) return;
    this._state = {
      ...this._state,
      activeCall: { ...this._state.activeCall, ...partial },
    };
    this._emit();
  }

  async _authHeaders() {
    const idToken = await getAuth().currentUser?.getIdToken();
    if (!idToken) throw new Error('Not signed in');
    return { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' };
  }

  // ── Register (lazy, idempotent) ───────────────────────────────────────
  async register() {
    if (this._state.status === 'registered') return this._state;
    if (this._connectingPromise) return this._connectingPromise;

    this._patch({ status: 'connecting', error: null });

    this._connectingPromise = (async () => {
      try {
        const headers = await this._authHeaders();
        const res = await fetch(`${API}/api/webrtc/token`, { headers });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.detail || `token mint failed (${res.status})`);

        this._patch({
          displayCallerId: body.displayCallerId || null,
          companyId:       body.companyId || null,
          uid:             body.uid || null,
        });

        const client = new TelnyxRTC({ login_token: body.jwtToken });

        client.on('telnyx.ready', () => {
          this._patch({ status: 'registered', error: null });
        });
        client.on('telnyx.error', (err) => {
          const msg = err?.error || err?.message || 'WebRTC error';
          this._patch({ status: 'error', error: msg });
        });
        client.on('telnyx.socket.close', () => {
          if (this._state.status === 'registered') this._patch({ status: 'idle' });
        });
        client.on('telnyx.notification', (n) => {
          // Diagnostic: always console.log every notification so we can see
          // exactly what Telnyx is delivering during a call. Phase 4.1.1
          // debugging hit a case where the browser never received an
          // inbound INVITE (state stuck at "trying"); this exposes whether
          // the SDK is getting ANY notification or none at all.
          // eslint-disable-next-line no-console
          console.log('[webrtc] notification', {
            type: n?.type,
            callId: n?.call?.id,
            state: n?.call?.state,
            direction: n?.call?.direction,
            remoteCallerNumber: n?.call?.options?.remoteCallerNumber,
          });

          // ── Top-level dispatch (Phase 4.2b) ───────────────────────────
          // CUSTOMER-INBOUND rings are routed to the separate
          // `_handleInboundRing` method BEFORE entering the stable
          // `_handleNotification` critical path. This keeps the
          // outbound flow byte-for-byte identical to the iter_193 baseline.
          // The dispatcher is gated by `WEBRTC_INBOUND_ENABLED` — when
          // off, all notifications flow into `_handleNotification` exactly
          // as in the stable outbound build.
          if (WEBRTC_INBOUND_ENABLED && this._isCustomerInboundRingNotification(n)) {
            this._handleInboundRing(n);
            return;
          }
          this._handleNotification(n);
        });

        await client.connect();
        this._client = client;

        // Phase 4.2a — presence heartbeat. While the SDK is registered,
        // ping the backend every 20s so the inbound router (Phase 4.2c)
        // knows this browser is live. This is a pure instrumentation
        // call today — it doesn't drive any routing yet, but starts
        // building the dataset Settings → Inbound Routing reads from.
        if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
        const beat = async () => {
          try {
            const headers = await this._authHeaders();
            await fetch(`${API}/api/webrtc/presence/ping`, { method: 'POST', headers });
          } catch (_) { /* silent — best-effort */ }
        };
        beat(); // fire one immediately so UI reflects fresh status fast
        this._heartbeatTimer = setInterval(beat, 20000);

        const ttl = Math.max(60, (body.expiresInSeconds || 3600) - 600);
        if (this._tokenRefreshTimer) clearTimeout(this._tokenRefreshTimer);
        this._tokenRefreshTimer = setTimeout(() => {
          // On expiry, force a fresh register cycle if currently idle.
          if (this._state.status === 'registered' && !this._activeCall) {
            this.disconnect();
          }
        }, ttl * 1000);

        return this._state;
      } catch (err) {
        this._patch({ status: 'error', error: err.message || String(err) });
        throw err;
      } finally {
        this._connectingPromise = null;
      }
    })();

    return this._connectingPromise;
  }

  // ════════════════════════════════════════════════════════════════════
  // Phase 4.2b — INBOUND RING HANDLER (separate critical path).
  //
  // This block is the ONLY place inbound-ring logic lives. It must
  // never be merged into `_handleNotification` and the dispatcher
  // above must remain the single entry-point that decides which
  // handler to invoke. Outbound flows never enter here.
  // ════════════════════════════════════════════════════════════════════

  /** Discriminator used by the SDK-notification dispatcher. Returns
   *  true ONLY for the small window when a SIP INVITE has reached the
   *  browser for a customer-originated call AND the operator has not
   *  yet accepted/rejected. After acceptance, subsequent notifications
   *  for the same call.id flow through the regular `_handleNotification`
   *  state machine.
   */
  _isCustomerInboundRingNotification(n) {
    if (n?.type !== 'callUpdate') return false;
    const call = n.call;
    if (!call) return false;
    const state = call.state || '';
    if (!['ringing', 'early', 'requesting', 'trying'].includes(state)) return false;

    const direction = (call.direction || '').toLowerCase();
    const isInbound =
      direction === 'inbound' || direction === 'incoming' ||
      Boolean(call.options?.remoteCallerNumber);
    if (!isInbound) return false;

    // Outbound bridge legs always have `_pendingDial` set in the brief
    // window before the first notification arrives — those go through
    // the existing critical path.
    if (this._pendingDial) return false;

    // If we already have an activeCall (post-accept transition or an
    // unrelated call in progress), defer to the state machine.
    if (this._state.activeCall && this._state.activeCall.callId === call.id) return false;
    if (this._state.activeCall && !this._state.activeCall.endedAt) return false;

    return true;
  }

  /** Renders the IncomingCallModal by setting `incomingCall` state.
   *  Idempotent — repeated notifications for the same callId no-op.
   *  Holds a reference to the SDK call object so accept/reject can
   *  invoke its methods.
   */
  _handleInboundRing(n) {
    const call = n.call;
    if (!call) return;
    const callId = call.id;
    this._inboundRingCallIds.add(callId);
    // Cache the SDK ref on a separate field — `_activeCall` is owned
    // by the outbound state machine and we MUST NOT touch it from here.
    this._inboundCall = call;

    if (this._state.incomingCall && this._state.incomingCall.callId === callId) {
      return; // already on screen
    }
    const fromNumber = call.options?.remoteCallerNumber || '';
    const fromName = call.options?.remoteCallerName || '';
    // eslint-disable-next-line no-console
    console.log('[webrtc] inbound customer call ringing', { callId, fromNumber });
    this._patch({
      incomingCall: {
        callId,
        fromNumber,
        fromName,
        startedAt: Date.now(),
      },
    });
  }

  /** Operator clicked Accept on the IncomingCallModal. We answer the
   *  SDK call object and clear our inbound-ring tracking. From this
   *  point on, the SDK's subsequent notifications (state='active',
   *  later 'destroy'/'purge', etc.) will flow through the regular
   *  `_handleNotification` state machine — which will create the
   *  activeCall, start the timer, and own teardown via the existing
   *  backend-authoritative path. We pre-seed activeCall here so the
   *  ActiveCallBar appears immediately while the SDK transitions.
   */
  acceptIncoming() {
    const inc = this._state.incomingCall;
    const c = this._inboundCall;
    if (!inc || !c) return;
    // Move the SDK ref into the outbound state machine's slot so the
    // hangup() method can act on the answered call.
    this._activeCall = c;
    this._inboundCall = null;
    this._inboundRingCallIds.delete(inc.callId);
    // Mark as answered so the regular auto-answer block in
    // `_handleNotification` (which ALSO answers any inbound notification
    // in ringing/early/requesting/trying state) will no-op for this id.
    this._answeredCallIds.add(inc.callId);

    try {
      // eslint-disable-next-line no-console
      console.log('[webrtc] accepting inbound call', { callId: inc.callId });
      const ret = c.answer();
      if (ret && typeof ret.then === 'function') {
        ret.catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[webrtc] inbound accept rejected', err);
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[webrtc] inbound accept raised', err);
    }

    // Pre-seed activeCall so the bar appears immediately.
    this._patch({
      incomingCall: null,
      activeCall: {
        callId: inc.callId,
        state: 'answering',
        destination: inc.fromNumber || '',
        conversationId: null,
        surface: 'inbound-webrtc',
        startedAt: null,
        endedAt: null,
        durationSecs: 0,
        remoteStream: null,
      },
    });
  }

  /** Operator clicked Decline. We hang up the SDK call (which fires
   *  the SIP REJECT — Telnyx then routes the call to the next leg in
   *  the simul-ring or to voicemail per existing PSTN logic — we do
   *  NOT touch any of that). Clears the modal.
   */
  rejectIncoming() {
    const c = this._inboundCall;
    const inc = this._state.incomingCall;
    if (inc) this._inboundRingCallIds.delete(inc.callId);
    try { c?.hangup?.(); } catch (_) { /* swallow */ }
    this._inboundCall = null;
    this._patch({ incomingCall: null });
  }

  // ════════════════════════════════════════════════════════════════════
  // END Phase 4.2b inbound block.
  // ════════════════════════════════════════════════════════════════════


  // ── Notification stream → call lifecycle ──────────────────────────────
  //
  // ⚠️  CRITICAL — STABLE OUTBOUND BASELINE (iter_196).
  //     This method is part of the verified-stable outbound path.
  //     Future inbound work MUST NOT add conditional branches here.
  //     If inbound ringing / accept UI is needed, add it as a SEPARATE
  //     dedicated method (e.g. `_handleInboundRing`) and dispatch to
  //     it via a CLEAR, EXPLICIT switch ABOVE this method's body —
  //     never as nested conditionals inside the auto-answer or
  //     state-machine blocks below. See iter_195's regression for why.
  //
  _handleNotification(n) {
    if (n?.type !== 'callUpdate') return;
    const call = n.call;
    if (!call) return;

    const next = call.state || 'unknown';
    const ts = Date.now();

    // ── Inbound auto-answer (proven iter_193 path) ──────────────────────
    // The Telnyx WebRTC SDK considers any Call Control App originated
    // leg as INBOUND from the browser's perspective. For backend-mediated
    // OUTBOUND bridges, we must answer ASAP (exactly once per call.id)
    // so `call.answered` fires and the backend can dial the customer leg.
    //
    // Customer-inbound ringing / accept flow is gated on
    // `_inboundRingEnabled` (off by default — kept inert until outbound
    // is fully verified stable). When off: auto-answer is the sole
    // inbound path — identical to the working iter_193 behavior.
    const direction = (call.direction || '').toLowerCase();
    const isInboundCall =
      direction === 'inbound' || direction === 'incoming' ||
      Boolean(call.options?.remoteCallerNumber);

    if (
      isInboundCall &&
      ['ringing', 'early', 'requesting', 'trying'].includes(next) &&
      !this._answeredCallIds.has(call.id)
    ) {
      this._answeredCallIds.add(call.id);
      try {
        // eslint-disable-next-line no-console
        console.log('[webrtc] auto-answering inbound call', { callId: call.id, state: next, direction });
        const ret = call.answer();
        if (ret && typeof ret.then === 'function') {
          ret.catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[webrtc] call.answer() rejected', err);
            this._answeredCallIds.delete(call.id);
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[webrtc] call.answer() raised', err);
        this._answeredCallIds.delete(call.id);
      }
    }

    // ── State machine ──────────────────────────────────────────────────
    const isNewCall = !this._state.activeCall || this._state.activeCall.callId !== call.id;
    if (isNewCall) {
      this._activeCall = call;
      const pending = this._pendingDial;
      this._state = {
        ...this._state,
        activeCall: {
          callId: call.id,
          state: next,
          destination: pending?.destination || call.options?.remoteCallerNumber || '',
          conversationId: pending?.conversationId || null,
          surface: pending?.surface || 'inbox',
          startedAt: null,
          endedAt: null,
          durationSecs: 0,
          remoteStream: null,
        },
      };
      this._pendingDial = null;
    } else {
      this._state = {
        ...this._state,
        activeCall: { ...this._state.activeCall, state: next },
      };
    }

    if (call.remoteStream && this._state.activeCall.remoteStream !== call.remoteStream) {
      this._state.activeCall.remoteStream = call.remoteStream;
    }

    if (next === 'active' && !this._state.activeCall.startedAt) {
      this._state.activeCall.startedAt = ts;
      this._startedLogged = true;
      this._startTick();
      this._logEvent('call_started_webrtc', { callId: call.id });
    }

    if (['hangup', 'destroy', 'purge'].includes(next)) {
      const wasActive = Boolean(this._state.activeCall.startedAt);

      // Important subtlety for bridged calls:
      //   When the backend issues the explicit `POST /v2/calls/{legA}/actions/bridge`
      //   to patch browser-leg audio into the PSTN-leg media path, Telnyx
      //   reparents the browser's SIP leg into a server-side media bridge.
      //   The `@telnyx/webrtc` SDK reacts by firing `destroy`/`purge` on the
      //   local call object — even though the call is still fully active
      //   on Telnyx's side and audio is flowing. If we teared down the UI
      //   on that SDK signal, the operator would lose the ActiveCallBar
      //   (and the Hang Up button with it) while the customer is still
      //   talking.
      //
      //   The backend's `webrtc_active/{uid}` Firestore doc is the
      //   authoritative lifecycle signal — it's written on initiate and
      //   deleted when any leg actually hangs up. The UI teardown is
      //   driven by `markCallEndedByBackend()` when the ActiveCallBar's
      //   onSnapshot listener sees the doc disappear. Here we merely
      //   release the SDK-side references + stop logging.
      if (this._startedLogged && !wasActive) {
        // Pre-active teardown (e.g. the remote side rejected the INVITE
        // before we ever reached active) — safe to log call_ended here.
        this._logEvent('call_ended_webrtc', {
          callId: call.id,
          durationSecs: 0,
          state: next,
        });
      }

      const callId = call.id;
      this._answeredCallIds.delete(callId);
      this._activeCall = null;

      if (!wasActive) {
        // Never reached active → UI can clear immediately; no backend
        // webrtc_active doc will ever appear.
        const prevCallId = this._state.activeCall?.callId;
        setTimeout(() => {
          if (this._state.activeCall && this._state.activeCall.callId === prevCallId) {
            this._patch({ activeCall: null });
          }
        }, 1500);
        this._startedLogged = false;
      } else {
        // Post-active SDK teardown: keep the UI live; mark the SDK-level
        // state so the bar shows "Bridged" or similar but the Hang Up
        // button stays clickable. Backend is the authoritative terminator.
        this._patchCall({ sdkDetached: true });
      }
    }

    this._emit();
  }

  /**
   * Called by the ActiveCallBar when the backend-authoritative
   * `webrtc_active/{uid}` Firestore doc is deleted (i.e. Telnyx webhook
   * ran `on_call_hangup`). This is the ONLY path that should tear down
   * the UI for a call that successfully reached the `active` state —
   * the SDK's own destroy/purge events are unreliable under server-side
   * bridge reparenting.
   */
  markCallEndedByBackend() {
    const ac = this._state.activeCall;
    if (!ac) return;
    // Compute final duration from whatever `startedAt` we captured.
    const ts = Date.now();
    const dur = ac.startedAt
      ? Math.max(0, Math.round((ts - ac.startedAt) / 1000))
      : (ac.durationSecs || 0);
    if (this._startedLogged) {
      this._logEvent('call_ended_webrtc', {
        callId: ac.callId,
        durationSecs: dur,
        state: 'ended_by_backend',
      });
    }
    this._stopTick();
    this._startedLogged = false;
    this._patchCall({ endedAt: ts, durationSecs: dur });
    const endedId = ac.callId;
    setTimeout(() => {
      if (this._state.activeCall && this._state.activeCall.callId === endedId) {
        this._patch({ activeCall: null });
      }
    }, 1500);
  }

  _startTick() {
    if (this._tickInterval) return;
    this._tickInterval = setInterval(() => {
      const ac = this._state.activeCall;
      if (!ac || !ac.startedAt || ac.endedAt) return;
      const dur = Math.max(0, Math.round((Date.now() - ac.startedAt) / 1000));
      if (dur !== ac.durationSecs) this._patchCall({ durationSecs: dur });
    }, 1000);
  }

  _stopTick() {
    if (this._tickInterval) {
      clearInterval(this._tickInterval);
      this._tickInterval = null;
    }
  }

  async _logEvent(event, extra = {}) {
    try {
      const headers = await this._authHeaders();
      const ac = this._state.activeCall;
      await fetch(`${API}/api/webrtc/log-event`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          event,
          customerPhone: ac?.destination || '',
          conversationId: ac?.conversationId || undefined,
          surface: ac?.surface || 'inbox',
          ...extra,
        }),
      });
    } catch (_) { /* fire-and-forget */ }
  }

  // ── Backend-mediated dial ────────────────────────────────────────────
  async placeCall({ destination, conversationId, surface }) {
    if (!destination) throw new Error('destination is required');
    if (this._state.status !== 'registered') await this.register();
    if (this._state.activeCall && !this._state.activeCall.endedAt) {
      throw new Error('Another WebRTC call is already in progress');
    }

    // Pre-seed a shell activeCall so the ActiveCallBar reflects
    // "Connecting…" immediately, BEFORE Telnyx rings the browser back.
    // The callId will be overwritten when the inbound notification
    // arrives with Telnyx's real id.
    this._pendingDial = { destination, conversationId: conversationId || null, surface: surface || 'inbox-webrtc' };
    this._state = {
      ...this._state,
      activeCall: {
        callId: `pending-${Date.now()}`,
        state: 'trying',
        destination,
        conversationId: conversationId || null,
        surface: surface || 'inbox-webrtc',
        startedAt: null,
        endedAt: null,
        durationSecs: 0,
        remoteStream: null,
      },
    };
    this._emit();

    try {
      const headers = await this._authHeaders();
      const res = await fetch(`${API}/api/webrtc/initiate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          destination,
          conversationId: conversationId || undefined,
          surface: surface || 'inbox-webrtc',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Clear the shell if the backend rejected us.
        this._patch({ activeCall: null });
        this._pendingDial = null;
        // Surface the FULL Telnyx error — FastAPI wraps our structured
        // detail under `body.detail`. Could be a string (simple errors)
        // or an object `{error, telnyx:{status, body, detail, request}}`.
        const d = body?.detail;
        let msg;
        let telnyx = null;
        if (typeof d === 'string') {
          msg = d;
        } else if (d && typeof d === 'object') {
          msg = d.error || `dial failed (${res.status})`;
          telnyx = d.telnyx || null;
        } else {
          msg = `dial failed (${res.status})`;
        }
        if (telnyx) {
          // eslint-disable-next-line no-console
          console.error('[webrtc] Telnyx rejected the call:', {
            httpStatus: res.status,
            telnyxStatus: telnyx.status,
            telnyxDetail: telnyx.detail,
            telnyxBody: telnyx.body,
            request: telnyx.request,
          });
        }
        const err = new Error(msg);
        err.telnyx = telnyx;
        throw err;
      }
      return body;
    } catch (err) {
      this._patch({ activeCall: null });
      this._pendingDial = null;
      throw err;
    }
  }

  hangup() {
    // Fire both: backend bridge teardown (authoritative; kills both
    // legs via Telnyx Call Control) AND the SDK's local hangup
    // (releases the browser's RTCPeerConnection cleanly). The backend
    // call is async/fire-and-forget so UI doesn't block on network.
    try { this._activeCall?.hangup?.(); } catch (_) { /* swallow */ }

    (async () => {
      try {
        const headers = await this._authHeaders();
        await fetch(`${API}/api/webrtc/hangup`, { method: 'POST', headers });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[webrtc] backend hangup failed (non-fatal)', err);
      }
    })();

    // If we're still in the pre-ring shell state with no SDK call yet,
    // just clear the shell so the UI doesn't get stuck.
    if (!this._activeCall && this._state.activeCall) {
      this._patch({ activeCall: null });
    }
  }

  /** Manual answer escape hatch for the dev page. Idempotent. */
  answer() {
    const c = this._activeCall;
    if (!c) return;
    try {
      // eslint-disable-next-line no-console
      console.log('[webrtc] manual answer requested', { callId: c.id, state: c.state });
      c.answer();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[webrtc] manual answer raised', err);
    }
  }


  disconnect() {
    try { this._activeCall?.hangup?.(); } catch (_) { /* swallow */ }
    try { this._inboundCall?.hangup?.(); } catch (_) { /* swallow */ }
    try { this._client?.disconnect?.(); } catch (_) { /* swallow */ }
    this._stopTick();
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    this._client = null;
    this._activeCall = null;
    this._inboundCall = null;
    this._pendingDial = null;
    this._inboundRingCallIds.clear();
    this._state = {
      status: 'idle', displayCallerId: null, companyId: null, uid: null,
      activeCall: null, incomingCall: null, error: null,
    };
    this._emit();
  }
}

export function getWebRTC() {
  if (!_instance) _instance = new WebRTCManager();
  return _instance;
}

export const WEBRTC_DEV_ENABLED = process.env.REACT_APP_WEBRTC_DEV === 'true';
export const WEBRTC_INBOUND_ENABLED = process.env.REACT_APP_WEBRTC_INBOUND_ENABLED === 'true';
